/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Helper: request Cache.
 */

import axios from 'axios';

/**
 * Request Cache and Deduplication Utility
 * 
 * Prevents duplicate API calls by caching responses for a short period (2 seconds)
 * This helps when multiple components make the same request simultaneously
 * 
 * Usage: Call setupRequestCache() once during app initialization
 */

const responseCache = new Map(); // key -> { response, timestamp }
const CACHE_TTL = 2000; // 2 seconds

/**
 * Creates a unique key for a request
 */
const createKey = (config) => {
  const { method = 'get', url, params, data } = config;
  const paramsStr = params ? JSON.stringify(params) : '';
  const dataStr = data ? JSON.stringify(data) : '';
  return `${method.toUpperCase()}:${url}:${paramsStr}:${dataStr}`;
};

/**
 * Setup request caching
 * Call this once during app initialization
 */
export const setupRequestCache = () => {
  // Request interceptor - check cache
  axios.interceptors.request.use(
    (config) => {
      // Skip caching if header is set
      if (config.headers?.['X-Skip-Cache'] === 'true') {
        delete config.headers['X-Skip-Cache'];
        config.__skipCache = true;
        return config;
      }

      const key = createKey(config);

      // Check response cache
      const cached = responseCache.get(key);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        // Mark this request as cached so response interceptor can return it
        config.__useCachedResponse = cached.response;
        return config;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - cache responses
  axios.interceptors.response.use(
    (response) => {
      // Return cached response if this request was marked to use cache
      if (response.config.__useCachedResponse) {
        return response.config.__useCachedResponse;
      }

      const key = createKey(response.config);

      // Don't cache if skip cache was set
      if (!response.config.__skipCache) {
        // Cache successful responses
        if (response.status >= 200 && response.status < 300) {
          responseCache.set(key, {
            response: response,
            timestamp: Date.now()
          });

          // Clean up cache after TTL
          setTimeout(() => {
            responseCache.delete(key);
          }, CACHE_TTL);
        }
      }

      return response;
    },
    (error) => {
      // Return cached response even on error if it was marked
      if (error.config?.__useCachedResponse) {
        return Promise.resolve(error.config.__useCachedResponse);
      }

      return Promise.reject(error);
    }
  );
};

/**
 * Clear the cache (useful for forced refresh)
 */
export const clearCache = () => {
  responseCache.clear();
};

/**
 * Clear cache for a specific URL pattern
 */
export const clearCacheForUrl = (urlPattern) => {
  for (const key of responseCache.keys()) {
    if (key.includes(urlPattern)) {
      responseCache.delete(key);
    }
  }
};
